import { Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { toSignal } from '@angular/core/rxjs-interop';
import { listEmails$, renderEmailByID$ } from '@vendasta/protos/emailbuilder/v1/emails';
import { EmailCategory } from '@vendasta/protos/emailbuilder/v1/enums';
import type { EmailBuilderEmail } from '@vendasta/protos/emailbuilder/v1/types';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

type TemplatePickerState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; rows: EmailBuilderEmail[] };

export interface TemplatePickerDialogData {
  namespace: string;
  emailCategories?: EmailCategory[];
  createTemplateUrl?: string;
}

@Component({
  selector: 'lib-template-picker-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    GalaxyFormFieldModule,
    DatePipe,
  ],
  templateUrl: './template-picker-dialog.component.html',
  styleUrls: ['./template-picker-dialog.component.scss'],
})
export class TemplatePickerDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<TemplatePickerDialogComponent, EmailBuilderEmail | null>);
  private readonly data = inject<TemplatePickerDialogData>(MAT_DIALOG_DATA);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly searchQuery = signal('');

  private readonly state$ = listEmails$({
    namespace: this.data.namespace,
    filters: {
      emailCategories: this.data.emailCategories ?? [],
    },
    projectionFilters: [],
    pagingOptions: { pageSize: 100, cursor: '' },
  }).pipe(
    map(
      (response): TemplatePickerState => ({
        status: 'loaded',
        rows: (response.emails ?? []).filter((e) => !!e.emailBuilderEmailId),
      }),
    ),
    catchError(() => of<TemplatePickerState>({ status: 'error' })),
    startWith<TemplatePickerState>({ status: 'loading' }),
  );

  protected readonly state = toSignal(this.state$, { initialValue: { status: 'loading' as const } });

  protected readonly filteredRows = computed(() => {
    const s = this.state();
    if (s.status !== 'loaded') return [];
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return s.rows;
    return s.rows.filter(
      (t) =>
        (t.displayName ?? '').toLowerCase().includes(q) || (t.emailSubject ?? '').toLowerCase().includes(q),
    );
  });

  private readonly previewMap$ = this.state$.pipe(
    switchMap((state) => {
      if (state.status !== 'loaded' || state.rows.length === 0) {
        return of(new Map<string, SafeHtml>());
      }
      return forkJoin(
        state.rows.map((email) =>
          renderEmailByID$({
            emailBuilderEmailId: email.emailBuilderEmailId,
            namespace: this.data.namespace,
          }).pipe(
            map((r) => [email.emailBuilderEmailId, this.sanitizer.bypassSecurityTrustHtml(r.renderedHtml)] as const),
            catchError(() => of([email.emailBuilderEmailId, null] as const)),
          ),
        ),
      ).pipe(
        map((pairs) => new Map(pairs.filter((p): p is [string, SafeHtml] => p[1] !== null))),
      );
    }),
    startWith(new Map<string, SafeHtml>()),
  );

  protected readonly previewMap = toSignal(this.previewMap$, { initialValue: new Map<string, SafeHtml>() });

  protected readonly createTemplateUrl = this.data.createTemplateUrl;

  protected selectTemplate(template: EmailBuilderEmail): void {
    this.dialogRef.close(template);
  }

  protected cancel(): void {
    this.dialogRef.close(null);
  }

  protected displayLabel(template: EmailBuilderEmail): string {
    return template.displayName || template.emailSubject || '—';
  }
}
